import { inject, Injectable } from '@angular/core';
import {
  type AdminDisableSecondFactorInput,
  type AdminDisableSecondFactorMutation as AdminDisableSecondFactorMutationMutation,
  type AdminDisableSecondFactorMutationVariables,
  type CreateSecurityQuestionInput,
  type CreateSecurityQuestionMutation as CreateSecurityQuestionMutationMutation,
  type CreateSecurityQuestionMutationVariables,
  type DisableSecondFactorWithSecurityKeyInput,
  type DisableSecondFactorWithSecurityKeyMutation as DisableSecondFactorWithSecurityKeyMutationMutation,
  type DisableSecondFactorWithSecurityKeyMutationVariables,
  type EnableSecondFactorMutation as EnableSecondFactorMutationMutation,
  type EnableSecondFactorMutationVariables,
  type ResetSecondFactorMutation as ResetSecondFactorMutationMutation,
  type ResetSecondFactorMutationVariables,
  type SendSecondFactorResetRequestInput,
  type SendSecondFactorResetRequestMutation as SendSecondFactorResetRequestMutationMutation,
  type SendSecondFactorResetRequestMutationVariables,
  type ViewerWithSecondFactorQuery,
  type ViewerWithSecondFactorQueryVariables,
} from '@reconcept-platform/shared/types';
import { map } from 'rxjs/operators';
import { SharedGraphQlService } from '../../services/shared-graphql.service';
import {
  AdminDisableSecondFactorMutation,
  CreateSecurityQuestionMutation,
  DisableSecondFactorWithSecurityKeyMutation,
  EnableSecondFactorMutation,
  ResetSecondFactorMutation,
  SendSecondFactorResetRequestMutation,
} from './otp.mutations';
import { viewerWithSecondFactorQuery } from './otp.queries';

@Injectable({
  providedIn: 'root',
})
export class OtpApi {
  private apolloGraphqlService = inject(SharedGraphQlService);

  watchViewerWithSecondFactor() {
    return this.apolloGraphqlService
      .watchQuery<ViewerWithSecondFactorQuery, ViewerWithSecondFactorQueryVariables>({
        query: viewerWithSecondFactorQuery,
      })
      .pipe(map((result) => result.data.viewer));
  }

  /**
   * Resets second factor on account, and returns new code to connect a device with
   */
  resetSecondFactor(password: string, userId: string) {
    return (
      this.apolloGraphqlService
        .mutateV2<ResetSecondFactorMutationMutation, ResetSecondFactorMutationVariables>({
          mutation: ResetSecondFactorMutation,
          variables: {
            input: {
              currentPassword: password,
            },
          },
          refetchQueries: [
            {
              query: viewerWithSecondFactorQuery,
            },
          ],
          update: (cache) => {
            cache.modify({
              id: userId,
              fields: {
                secondFactor(existingRef, { DELETE }) {
                  return DELETE;
                },
              },
            });
          },
        })
        // @ts-expect-error TS18048 possibly undefined
        .pipe(map((result) => result.data.resetSecondFactor))
    );
  }

  enableSecondFactor(otp: string) {
    return (
      this.apolloGraphqlService
        .mutateV2<EnableSecondFactorMutationMutation, EnableSecondFactorMutationVariables>({
          mutation: EnableSecondFactorMutation,
          variables: {
            input: {
              otp,
            },
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.enableSecondFactor.secondFactor))
    );
  }

  disableSecondFactorWithSecurityKey(disableSecondFactorWithSecurityKeyInput: DisableSecondFactorWithSecurityKeyInput) {
    return (
      this.apolloGraphqlService
        .mutateV2<
          DisableSecondFactorWithSecurityKeyMutationMutation,
          DisableSecondFactorWithSecurityKeyMutationVariables
        >({
          mutation: DisableSecondFactorWithSecurityKeyMutation,
          variables: {
            input: disableSecondFactorWithSecurityKeyInput,
          },
        })
        // @ts-expect-error TS18048 possibly undefined
        .pipe(map((result) => result.data.disableSecondFactorWithSecurityKey))
    );
  }

  createSecurityQuestion(createSecurityQuestionInput: CreateSecurityQuestionInput) {
    return (
      this.apolloGraphqlService
        .mutateV2<CreateSecurityQuestionMutationMutation, CreateSecurityQuestionMutationVariables>({
          mutation: CreateSecurityQuestionMutation,
          variables: {
            input: createSecurityQuestionInput,
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.createSecurityQuestion.securityQuestion))
    );
  }

  sendSecondFactorResetRequest(sendSecondFactorResetRequestInput: SendSecondFactorResetRequestInput) {
    return (
      this.apolloGraphqlService
        .mutateV2<SendSecondFactorResetRequestMutationMutation, SendSecondFactorResetRequestMutationVariables>({
          mutation: SendSecondFactorResetRequestMutation,
          variables: {
            input: sendSecondFactorResetRequestInput,
          },
        })
        // @ts-expect-error TS18048 possibly undefined
        .pipe(map((result) => result.data.sendSecondFactorResetRequest))
    );
  }

  adminDisableSecondFactor(adminDisableSecondFactorInput: AdminDisableSecondFactorInput) {
    return (
      this.apolloGraphqlService
        .mutateV2<AdminDisableSecondFactorMutationMutation, AdminDisableSecondFactorMutationVariables>({
          mutation: AdminDisableSecondFactorMutation,
          variables: {
            input: adminDisableSecondFactorInput,
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.adminDisableSecondFactor.secondFactor))
    );
  }
}
